import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Collapse } from 'antd';
import { Play, Pause } from 'lucide-react';
import './OverviewCard.scss';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import {
	ChatOutlined,
	LinkOutlined,
	PhoneForwardedOutlined,
	PinchOutlined,
	PsychologyOutlined,
	QuestionAnswerOutlined,
	TipsAndUpdatesOutlined,
	HandshakeOutlined,
	TerminalOutlined,
	CallEnd,
	PhoneMissed,
	Api,
	CalendarMonthOutlined,
	MessageOutlined,
	WebhookOutlined,
	VolumeUpOutlined,
	AnalyticsOutlined,
} from '@mui/icons-material';
import { InfoCircleOutlined } from '@ant-design/icons';
import { VoiceAndLanguageInfo } from './VoiceAndLanguage/VoiceAndLanguageInfo';
import { QuestionAndAnswerInfo } from './QuestionAndAnswer/QuestionAndAnswerInfo';
import { KnowledgeBaseInfo } from './KnowledgeBase/KnowledgeBaseInfo';
import { TweakAdvancedSettingsInfo } from './TweakAdvancedSettings/TweakAdvancedSettingsInfo';
import { TextingWorkflowInfo } from './TextingWorkflow/TextingWorkflowInfo';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	deletePostCallWebhook,
	deletePostCallMessage,
	getUser,
	updatePostCallWebhook,
	updatePostCallMessage,
	updateUser,
} from '@/api/user';
import { PhoneWorkflowInfo } from './PhoneWorkflow/PhoneWorkflowInfo';
import { CallTransferingWorkflowInfo } from './CallTransferingWorkflow/CallTransferingWorkflowInfo';
import { GreetingPhraseInfo } from './GreetingPhrase/GreetingPhraseInfo';
import { AiPromptInfo } from './AiPrompt/AiPromptInfo';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import {
	getResellerUserConfigureGating,
	getResellerUserConfigureVisibility,
	getSelectedAgentId,
} from '@/store/account/selector';
import { PostCallWebhook } from './PostCallWebhook/PostCallWebhook';
import { ItemType as CollapseItemType } from 'rc-collapse/lib/interface';
import { OverViewCards } from 'src/enums/EOverViewCards';
import { useSearchParams } from 'react-router-dom';
import { ApiWorkflowInfo } from './ApiWorkflow/ApiWorkflowInfo';
import { useDispatch } from 'react-redux';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { getResellerData } from '@/store/reseller/selector';
import { EFeatureCategories } from 'src/enums/EFeatureCategories';
import { EFeatureKeys } from 'src/enums/EFeatureKeys';
// import { TextAssistInfo } from './TextAssist/TextAssistInfo';
import { PostCallMessageInfo } from './PostCallMessage/PostCallMessageInfo';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import ZapierIntegrationForm from './TweakAdvancedSettings/ZapierIntegration/ZapierIntegrationForm/ZapierIntegrationForm';
// import { CallResponseConfigInfo } from './VoiceMailMessage/CallResponseConfigInfo';
// import { EmailWorkflowInfo } from './EmailWorkflow/EmailWorkflow';
// import { Mail } from 'lucide-react';
import { FeaturedVoicesInfo } from './FeaturedVoices/FeaturedVoices';
import PostCallAnalysis from '../../LogsComponent/CallLogs/PostCallAnalysis';
import { initializeScenarioCounts } from '@/utils/scenarioCountInitializer';
import { setScenarioCounts, setCalendarCount } from '@/store/scenarioCounts/slice';
import { getCalendars } from '@/api/calendar';
import { safeJSONParse } from '@/utils/helper';

// Import audio samples
import callTransferSample from '../../../../assets/sounds/samples/call-transfer-workflow-sample.mp3';
import textingSample from '../../../../assets/sounds/samples/texting-workflow-sample.mp3';
import intakeFormSample from '../../../../assets/sounds/samples/intake-form-workflow-sample.mp3';
import { setSelectedAgent } from '@/store/account/slice';
import { useAudioPlayer } from '@/components/OnboardingModal/stages/hooks/useAudioPlayer';
import { CalendarWorkflowContainer } from './CalendarWorkflow/CalendarWorkflowContainer';
import { FeatureGate } from '@/components/FeatureGate';
import { AppRoutes } from 'src/enums/ERoutes';

interface CustomCollapseItem extends Omit<CollapseItemType, 'children'> {
	key: OverViewCards;
	label: React.ReactNode;
	children: React.ReactNode;
}

type OverviewCardProps = {
	onCompletionChange: (completed: number, total: number) => void;
	debrand: boolean;
	isNoviUser: boolean;
	apiWorkflowRef?: React.RefObject<HTMLDivElement>;
	gcalRef?: React.RefObject<HTMLDivElement>;
	intakeRef?: React.RefObject<HTMLDivElement>;
	resellerView?: boolean;
	isStandaloneCampaign?: boolean;
};

const getValidItems = (items: CustomCollapseItem[], allowedKeys: OverViewCards[]) => {
	return items
		.filter((item) => allowedKeys.includes(item.key))
		.map((item) => {
			// Get the description from the LabelWithDescription, regardless of nesting
			const findDescription = (element: React.ReactElement): string => {
				if (element.props.description) {
					return element.props.description;
				}
				if (element.props.children && React.isValidElement(element.props.children)) {
					return findDescription(element.props.children);
				}
				return '';
			};

			const labelElement = item.label as React.ReactElement;
			const description = findDescription(labelElement);

			// Determine if this item should have a sample audio player
			const getSampleAudioData = (key: OverViewCards) => {
				switch (key) {
					case OverViewCards.CALL_TRANSFERRING_WORKFLOW:
						return {
							audioSrc: callTransferSample,
							title: 'Sample Call Transfer',
							description: 'Listen to how our AI transfers calls professionally',
						};
					case OverViewCards.TEXTING_WORKFLOW:
						return {
							audioSrc: textingSample,
							title: 'Sample Text Response',
							description: 'Listen to how our AI handles texting scenarios',
						};
					case OverViewCards.PHONE_FORM_WORKFLOW:
						return {
							audioSrc: intakeFormSample,
							title: 'Sample Customer Info Collection',
							description: 'Listen to how our AI collects customer information',
						};
					default:
						return null;
				}
			};

			const audioData = getSampleAudioData(item.key);

			return {
				...item,
				children: (
					<>
						<div className="mb-6 text-gray-600 text-base bg-blue-50/30 px-6 py-4 rounded-xl flex items-start gap-3 border border-blue-100/50">
							<InfoCircleOutlined className="text-blue-600 mt-1 text-xs" />
							<span className="font-normal">{description}</span>
						</div>
						{audioData && (
							<SampleAudioPlayer
								audioSrc={audioData.audioSrc}
								title={audioData.title}
								description={audioData.description}
							/>
						)}
						{item.children}
					</>
				),
			};
		});
};

// First, let's create a reusable badge component at the top of the file
const StatusBadge: React.FC<{
	type: 'required' | 'recommended' | 'optional' | 'new' | 'beta' | 'advanced' | 'enabled';
}> = ({ type }) => {
	const styles = {
		required: 'bg-red-50 text-red-600 border border-red-100',
		recommended: 'bg-green-50 text-green-700 border border-green-100',
		optional: 'bg-gray-50 text-gray-600 border border-gray-100',
		new: 'bg-blue-50 text-blue-700 border border-blue-100',
		beta: 'bg-purple-50 text-purple-700 border border-purple-100',
		advanced: 'bg-amber-50 text-amber-700 border border-amber-100',
		enabled: 'bg-green-50 text-green-700 border border-green-100',
	};

	return (
		<span className={`px-3 py-1 text-xs font-medium rounded-full ${styles[type]}`}>
			{type.charAt(0).toUpperCase() + type.slice(1)}
		</span>
	);
};

const SampleAudioPlayer: React.FC<{ audioSrc: string; title: string; description: string }> = ({
	audioSrc,
	title: _title,
	description,
}) => {
	const { audioRef, isPlaying, currentTime, duration, isLoading, togglePlayPause, formatTime } =
		useAudioPlayer({ audioSrc });

	return (
		<div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-sm">
			{/* Hidden audio element */}
			<audio ref={audioRef} src={audioSrc} />

			<h3 className="text-sm font-medium text-gray-700 mb-2">{description}</h3>
			<div className="flex items-center gap-3 bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-200">
				<button
					className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 flex items-center justify-center transition-all duration-200 shadow-sm"
					onClick={togglePlayPause}
					disabled={isLoading}
				>
					{isLoading ? (
						<div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
					) : isPlaying ? (
						<Pause className="w-4 h-4 text-white" />
					) : (
						<Play className="w-4 h-4 text-white ml-0.5" />
					)}
				</button>

				<div className="flex-1 relative">
					<div className="h-1.5 bg-blue-200/40 rounded-full overflow-hidden">
						<div
							className="h-1.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-100"
							style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
						/>
					</div>
				</div>

				<span className="text-xs text-blue-600/70 font-medium min-w-[30px]">
					{formatTime(currentTime)}
				</span>
			</div>
		</div>
	);
};

const OverviewCard: React.FC<OverviewCardProps> = ({
	onCompletionChange,
	debrand,
	isNoviUser,
	apiWorkflowRef,
	gcalRef,
	intakeRef,
}) => {
	const dispatch = useDispatch();
	const [searchParams] = useSearchParams();
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const tweakAdvancedSettingsRef = useRef<HTMLDivElement>(null);

	const [activeKey, setActiveKey] = useState<string | string[] | undefined>(undefined); // Setting default open key

	const hiddenConfigureItems = useAppSelector(getResellerUserConfigureVisibility) ?? [];

	const gatedConfigureItems = useAppSelector(getResellerUserConfigureGating) ?? [];

	const currentReseller = useAppSelector(getResellerData);

	const isPostCallMessageFeatureEnabled = useFeatureFlag(FeatureFlag.POST_CALL_TEXT_BACK);

	const { data, refetch, isRefetching } = useQuery({
		queryKey: ['receptionist'],
		queryFn: () => getUser(selectedAgentId as string),
		enabled: !!selectedAgentId,
	});

	const { data: calendarData } = useQuery({
		queryKey: ['get-calendar', selectedAgentId],
		queryFn: () => getCalendars(selectedAgentId),
		enabled: !!selectedAgentId,
	});

	useEffect(() => {
		if (data) {
			dispatch(setSelectedAgent({ user: data }));
		}
	}, [data, refetch]);

	useEffect(() => {
		if (data && !isRefetching) {
			refetch();
		}
	}, [selectedAgentId]);

	const mutation = useMutation({
		mutationKey: ['update-query'],
		mutationFn: updateUser,
	});

	useEffect(() => {
		const encAcc = searchParams.get('encAcc');
		const encRefr = searchParams.get('encRefr');
		if (encAcc && encRefr) {
			setActiveKey(OverViewCards.CALENDAR_SCHEDULING_WORKFLOW);
		}
	}, [searchParams]);

	const postCallWebhookMutation = useMutation({
		mutationKey: ['post-call-webhook'],
		mutationFn: updatePostCallWebhook,
	});

	const deletePostCallWebhookMutation = useMutation({
		mutationKey: ['delete-post-call-webhook'],
		mutationFn: deletePostCallWebhook,
	});

	const postCallMessageMutation = useMutation({
		mutationKey: ['post-call-message'],
		mutationFn: updatePostCallMessage,
	});

	const deletePostCallMessageMutation = useMutation({
		mutationKey: ['delete-post-call-message'],
		mutationFn: deletePostCallMessage,
	});

	const isNonEmptyArray = (arr: any[] | null | undefined): boolean => {
		return Array.isArray(arr) && arr.length > 0;
	};

	const allItems: CustomCollapseItem[] = [
		{
			key: OverViewCards.GREETING_PHRASE,
			label: (
				<LabelWithDescription
					icon={
						<div className="p-1.5 bg-blue-50/50 rounded-lg">
							<HandshakeOutlined className="text-blue-600" />
						</div>
					}
					badges={<StatusBadge type="recommended" />}
					label="Greeting Phrase"
					description={
						isNoviUser
							? 'This is the opening line your AI Agent will use when making a call.'
							: 'This is the opening line your receptionist will use when answering the phone.'
					}
				/>
			),
			children: <GreetingPhraseInfo refetch={refetch} data={data} isNoviUser={isNoviUser} />,
		},
		{
			key: OverViewCards.ZAPIER_INTEGRATION,
			label: (
				<LabelWithDescription
					icon={
						<div className="p-1.5 bg-blue-50/50 rounded-lg">
							<WebhookOutlined className="text-blue-600" />
						</div>
					}
					badges={<StatusBadge type={data?.calllogwebhook ? 'enabled' : 'recommended'} />}
					label="Zapier Integration"
					description="Connect your AI Receptionist to 6000+ apps to automate your workflow after each call."
				/>
			),
			children: (
				<FeatureGate
					categoryKey={EFeatureCategories.ADVANCED_CAPABILITIES}
					featureKey={EFeatureKeys.ZAPIER_INTEGRATION}
					featureType="boolean"
					variant="overlay"
				>
					<ZapierIntegrationForm
						debrand={debrand}
						enabled={data?.calllogwebhook && data?.calllogwebhook !== 'DUMMY_URL' ? true : false}
					/>
				</FeatureGate>
			),
		},
		{
			key: OverViewCards.SMART_CALL_ANALYSIS,
			label: (
				<LabelWithDescription
					icon={
						<div className="p-1.5 bg-purple-50/50 rounded-lg">
							<AnalyticsOutlined className="text-blue-600" />
						</div>
					}
					badges={<StatusBadge type="enabled" />}
					label="Smart Call Analysis"
					description="AI-powered summaries and outcomes generated automatically after each call."
				/>
			),
			children: <PostCallAnalysis />,
		},
		{
			key: OverViewCards.AI_PROMPT,
			label: (
				<LabelWithDescription
					icon={
						<div className="p-1.5 bg-blue-50/50 rounded-lg">
							<TerminalOutlined className="text-blue-600" />
						</div>
					}
					badges={<StatusBadge type="recommended" />}
					label="Custom Commands"
					description={
						isNoviUser
							? 'A prompt for your AI Agent. Generally, this default prompt is fine for most use cases.'
							: 'A prompt for your AI Receptionist. Generally, this default prompt is fine for most use cases.'
					}
				/>
			),
			children: (
				<AiPromptInfo mutation={mutation} refetch={refetch} data={data} isNoviUser={isNoviUser} />
			),
		},
		{
			key: OverViewCards.KNOWLEDGE_BASE,
			label: (
				<LabelWithDescription
					icon={
						<div className="p-1.5 bg-blue-50/50 rounded-lg">
							<PsychologyOutlined className="text-blue-600" />
						</div>
					}
					badges={<StatusBadge type="recommended" />}
					label="Business Information"
					description="Provide necessary information to AI using simple text, website URL or documents."
				/>
			),
			children: <KnowledgeBaseInfo data={data} refetch={refetch} isNoviUser={isNoviUser} />,
		},
		{
			key: OverViewCards.CALL_TRANSFERRING_WORKFLOW,
			label: (
				<LabelWithDescription
					icon={
						<div className="p-1.5 bg-blue-50/50 rounded-lg">
							<PhoneForwardedOutlined className="text-blue-600" />
						</div>
					}
					badges={<StatusBadge type="recommended" />}
					label="Transfer Call"
					description={
						isNoviUser
							? 'Teach your AI Agent how to intelligently transfer the call.'
							: 'Teach your AI Receptionist how to intelligently transfer the call.'
					}
				/>
			),
			children: (
				<FeatureGate
					categoryKey={EFeatureCategories.WORKFLOWS}
					featureKey={EFeatureKeys.CALL_TRANSFER_WORKFLOWS}
					featureType="numeric"
					data={data}
					elementIds={['add-call-transfer-workflow-btn', 'duplicate-call-transfer-workflow-btn']}
					variant="inline"
					usageKey="callWorkflows"
					title="Call Transfer Workflows"
				>
					<CallTransferingWorkflowInfo
						mutation={mutation}
						data={data}
						refetch={refetch}
						debrand={debrand}
					/>
				</FeatureGate>
			),
		},
		{
			key: OverViewCards.TEXTING_WORKFLOW,
			label: (
				<LabelWithDescription
					icon={
						<div className="p-1.5 bg-blue-50/50 rounded-lg">
							<ChatOutlined className="text-blue-600" />
						</div>
					}
					badges={<StatusBadge type="recommended" />}
					label="Send Text Message"
					description={
						isNoviUser
							? 'Your AI Agent can text the caller in real-time during the call. Great for sending Calendly links and menus.'
							: 'Your AI Receptionist can text the caller in real-time during the call. Great for sending Calendly links and menus.'
					}
				/>
			),
			children: (
				<FeatureGate
					categoryKey={EFeatureCategories.WORKFLOWS}
					featureKey={EFeatureKeys.TEXT_WORKFLOWS}
					featureType="numeric"
					data={data}
					elementIds={['add-text-workflow-btn', 'duplicate-text-workflow-btn']}
					variant="inline"
					usageKey="workflows"
					title="Texting Workflows"
				>
					<TextingWorkflowInfo
						mutation={mutation}
						data={data}
						refetch={refetch}
						debrand={debrand}
						isNoviUser={isNoviUser}
					/>
				</FeatureGate>
			),
		},
		// isNoviUser &&
		// 	!resellerView &&
		// 	!isStandaloneCampaign && {
		// 		key: OverViewCards.EMAIL_WORKFLOW,
		// 		label: (
		// 			<LabelWithDescription
		// 				icon={
		// 					<div className="p-1.5 bg-blue-50/50 rounded-lg">
		// 						<Mail className="text-blue-600" />
		// 					</div>
		// 				}
		// 				badges={<StatusBadge type="recommended" />}
		// 				label="Send Email"
		// 				description="Your AI Agent can send emails to callers during the conversation—perfect for sharing links, documents, or summaries instantly."
		// 			/>
		// 		),
		// 		children: (
		// 			<EmailWorkflowInfo
		// 				mutation={mutation}
		// 				data={data}
		// 				refetch={refetch}
		// 				debrand={debrand}
		// 				isNoviUser={isNoviUser}
		// 			/>
		// 		),
		// 	},
		{
			key: OverViewCards.PHONE_FORM_WORKFLOW,
			label: (
				<div ref={intakeRef}>
					<LabelWithDescription
						icon={
							<div className="p-1.5 bg-blue-50/50 rounded-lg">
								<TipsAndUpdatesOutlined className="text-blue-600" />
							</div>
						}
						badges={<StatusBadge type="recommended" />}
						label="Collect Information (Intake Form)"
						description={
							isNoviUser
								? 'Your AI Agent can collect information from your callers and store it separately.'
								: 'Your AI receptionist can collect information from your callers and store it separately.'
						}
					/>
				</div>
			),
			children: (
				<FeatureGate
					categoryKey={EFeatureCategories.WORKFLOWS}
					featureKey={EFeatureKeys.INTAKE_FORM_WORKFLOWS}
					featureType="numeric"
					data={{
						intakeFormWorkflows: [
							...safeJSONParse(data?.intake_questions_workflows, []),
							...safeJSONParse(data?.intake_questions, []),
						],
					}}
					elementIds={['add-intake-form-workflow-btn', 'duplicate-intake-form-workflow-btn']}
					variant="inline"
					usageKey="intakeFormWorkflows"
					title="Intake Form Workflows"
				>
					<PhoneWorkflowInfo mutation={mutation} data={data} refetch={refetch} debrand={debrand} />
				</FeatureGate>
			),
		},
		// isNoviUser && {
		// 	key: OverViewCards.TEXT_ASSIST,
		// 	label: (
		// 		<LabelWithDescription
		// 			icon={
		// 				<div className="p-1.5 bg-blue-50/50 rounded-lg">
		// 					<ChatOutlined className="text-blue-600" />
		// 				</div>
		// 			}
		// 			label="Text Assist"
		// 			description="Your AI Agent can send text messages to leads who do not answer the phone."
		// 			badges={<StatusBadge type="recommended" />}
		// 		/>
		// 	),
		// 	children: <TextAssistInfo data={data} refetch={refetch} />,
		// },
		// isNoviUser && {
		// 	key: OverViewCards.CALL_RESPONSE_CONFIG,
		// 	label: (
		// 		<LabelWithDescription
		// 			icon={
		// 				<div className="p-1.5 bg-blue-50/50 rounded-lg">
		// 					<SettingOutlined className="text-blue-600" />
		// 				</div>
		// 			}
		// 			label="Call Settings"
		// 			badges={<StatusBadge type={'recommended'} />}
		// 			description="Configure how the AI Agents responds when calls are answered by voicemail systems."
		// 		/>
		// 	),
		// 	children: <CallResponseConfigInfo data={data} refetch={refetch} />,
		// },
		{
			key: OverViewCards.CALENDAR_SCHEDULING_WORKFLOW,
			label: (
				<div ref={gcalRef}>
					<LabelWithDescription
						icon={
							<div className="p-1.5 bg-blue-50/50 rounded-lg">
								<CalendarMonthOutlined className="text-blue-600" />
							</div>
						}
						badges={<StatusBadge type="recommended" />}
						label="Schedule an Appointment"
						description={'Integrate with Calendar for real-time appointment scheduling.'}
					/>
				</div>
			),
			children: (
				<CalendarWorkflowContainer
					clientObject={data}
					isNoviUser={isNoviUser}
					debrand={debrand}
					mutation={mutation}
					refetch={refetch}
				/>
			),
		},
		{
			key: OverViewCards.SAMPLE_QUESTIONS_ANSWERS,
			label: (
				<LabelWithDescription
					icon={
						<div className="p-1.5 bg-blue-50/50 rounded-lg">
							<QuestionAnswerOutlined className="text-blue-600" />
						</div>
					}
					badges={<StatusBadge type="recommended" />}
					label="Common Questions"
					description={
						isNoviUser
							? 'Frequently asked questions that your AI Agent can reference.'
							: 'Frequently asked questions that your AI Receptionist can reference.'
					}
				/>
			),
			children: <QuestionAndAnswerInfo refetch={refetch} data={data} isNoviUser={isNoviUser} />,
		},
		!isNoviUser &&
			isPostCallMessageFeatureEnabled && {
				key: OverViewCards.POST_CALL_MESSAGE,
				label: (
					<LabelWithDescription
						icon={
							<div className="p-1.5 bg-blue-50/50 rounded-lg">
								<PhoneMissed className="text-blue-600" />
							</div>
						}
						badges={<StatusBadge type="recommended" />}
						label="Send a Text to Caller"
						description="If your customer hangs up on your AI receptionist, send them a text to try to engage them. Your texting AI will continue the conversation over text."
					/>
				),
				children: (
					<FeatureGate
						categoryKey={EFeatureCategories.WORKFLOWS}
						featureKey={EFeatureKeys.POST_CALL_MESSAGE}
						featureType="numeric"
						data={data}
						elementIds={['add-post-call-message-btn']}
						variant="inline"
						usageKey="postCallMessage"
						title="Post Call Message"
					>
						<PostCallMessageInfo
							data={data}
							mutation={postCallMessageMutation}
							deleteMutation={deletePostCallMessageMutation}
							refetch={refetch}
							debrand={debrand}
						/>
					</FeatureGate>
				),
			},
		{
			key: OverViewCards.API_WORKFLOW,
			label: (
				<div ref={apiWorkflowRef}>
					<LabelWithDescription
						icon={
							<div className="p-1.5 bg-blue-50/50 rounded-lg">
								<Api className="text-blue-600" />
							</div>
						}
						badges={<StatusBadge type="advanced" />}
						label="API Integration"
						description={
							isNoviUser
								? 'Your AI Agent can make real-time API requests during the phone call. She can retrieve and submit information to your APIs realtime.'
								: 'Your AI Receptionist can make real-time API requests during the phone call. She can retrieve and submit information to your APIs realtime.'
						}
					/>
				</div>
			),
			children: (
				<FeatureGate
					categoryKey={EFeatureCategories.WORKFLOWS}
					featureKey={EFeatureKeys.API_WORKFLOWS}
					featureType="numeric"
					data={data}
					elementIds={['add-api-workflow-btn', 'duplicate-api-workflow-btn']}
					variant="inline"
					usageKey="call_webhook_workflows"
					message="To unlock API workflows, please contact us. We offer custom API workflow setups for enterprise accounts."
					actionButton={{
						text: 'Contact Sales',
						onClick: () => {
							window.open(
								`${import.meta.env.VITE_APP_BASE_URL}${AppRoutes.CONTACT_SALES}`,
								'_blank',
								'noreferrer',
							);
						},
						variant: 'primary',
					}}
				>
					<ApiWorkflowInfo mutation={mutation} data={data} refetch={refetch} debrand={debrand} />
				</FeatureGate>
			),
		},
		{
			key: OverViewCards.POST_CALL_WEBHOOK,
			label: (
				<LabelWithDescription
					icon={
						<div className="p-1.5 bg-blue-50/50 rounded-lg">
							<Api className="text-blue-600" />
						</div>
					}
					label="Custom Webhook"
					badges={<StatusBadge type="advanced" />}
					description="Send call data to your own webhook endpoint after each call."
				/>
			),
			children: (
				<FeatureGate
					categoryKey={EFeatureCategories.WEBHOOKS}
					featureKey={EFeatureKeys.CUSTOM_WEBHOOKS}
					featureType="numeric"
					data={data}
					elementIds={['add-custom-webhook-btn']}
					variant="overlay"
					usageKey="postCallWebhook"
				>
					<PostCallWebhook
						data={data}
						mutation={postCallWebhookMutation}
						deleteMutation={deletePostCallWebhookMutation}
						refetch={refetch}
						debrand={debrand}
					/>
				</FeatureGate>
			),
		},
		{
			key: OverViewCards.CUSTOMIZE_VOICE_LANGUAGES,
			label: (
				<LabelWithDescription
					icon={
						<div className="p-1.5 bg-blue-50/50 rounded-lg">
							<LinkOutlined className="text-blue-600 rotate-45" />
						</div>
					}
					badges={<StatusBadge type="recommended" />}
					label="Languages"
					description={
						isNoviUser
							? 'Turn your AI Agent into a multi-lingual superstar.'
							: 'Turn your AI Receptionist into a multi-lingual superstar.'
					}
				/>
			),
			children: <VoiceAndLanguageInfo refetch={refetch} data={data} debrand={debrand} />,
		},
		{
			key: OverViewCards.CHOOSE_VOICE,
			label: (
				<div className="cursor-pointer">
					<LabelWithDescription
						icon={
							<div className="p-1.5 bg-blue-50/50 rounded-lg">
								<VolumeUpOutlined className="text-blue-600" />
							</div>
						}
						badges={<StatusBadge type="recommended" />}
						label="Choose Voice"
						description={
							isNoviUser
								? 'Choose the voice your AI Agent will use when making calls.'
								: 'Choose the voice your AI Receptionist will use when answering the phone.'
						}
					/>
				</div>
			),
			children: <FeaturedVoicesInfo refetch={refetch} data={data} debrand={debrand} />,
		},
		{
			key: OverViewCards.TWEAK_ADVANCED_SETTINGS,
			label: (
				<LabelWithDescription
					icon={
						<div className="p-1.5 bg-blue-50/50 rounded-lg">
							<PinchOutlined className="text-blue-600" />
						</div>
					}
					badges={<StatusBadge type="advanced" />}
					label="Advanced Settings"
					description="Tweak settings like Extension Digits, Call Recordings, and more."
				/>
			),
			children: (
				<div ref={tweakAdvancedSettingsRef}>
					<TweakAdvancedSettingsInfo
						data={data}
						refetch={refetch}
						debrand={debrand}
						isNoviUser={isNoviUser}
					/>
				</div>
			),
		},
	].filter(Boolean) as CustomCollapseItem[];

	const items = useMemo<CustomCollapseItem[]>(() => {
		// Create mapping between OverViewCards and Configure keys
		const configureKeyMapping: { [key in OverViewCards]?: string } = {
			[OverViewCards.GREETING_PHRASE]: 'CONFIGURE.GREETING_PHRASE',
			[OverViewCards.AI_PROMPT]: 'CONFIGURE.AI_PROMPT',
			[OverViewCards.KNOWLEDGE_BASE]: 'CONFIGURE.KNOWLEDGE_BASE',
			[OverViewCards.CALL_TRANSFERRING_WORKFLOW]: 'CONFIGURE.CALL_TRANSFERRING',
			[OverViewCards.TEXTING_WORKFLOW]: 'CONFIGURE.TEXTING_WORKFLOW',
			[OverViewCards.EMAIL_WORKFLOW]: 'CONFIGURE.EMAIL_WORKFLOW',
			[OverViewCards.PHONE_FORM_WORKFLOW]: 'CONFIGURE.INTAKE_FORM',
			[OverViewCards.CALENDAR_SCHEDULING_WORKFLOW]: 'CONFIGURE.CALENDAR_SCHEDULING_WORKFLOW',
			[OverViewCards.SAMPLE_QUESTIONS_ANSWERS]: 'CONFIGURE.QA_SECTION',
			[OverViewCards.API_WORKFLOW]: 'CONFIGURE.API_WORKFLOW',
			[OverViewCards.POST_CALL_WEBHOOK]: 'CONFIGURE.POST_CALL_WEBHOOK',
			[OverViewCards.CUSTOMIZE_VOICE_LANGUAGES]: 'CONFIGURE.CUSTOMIZE_LANGUAGES',
			[OverViewCards.TWEAK_ADVANCED_SETTINGS]: 'CONFIGURE.ADVANCED_SETTINGS',
			[OverViewCards.ZAPIER_INTEGRATION]: 'CONFIGURE.ZAPIER_INTEGRATION',
			[OverViewCards.SMART_CALL_ANALYSIS]: 'CONFIGURE.SMART_CALL_ANALYSIS',
		};

		// First filter out hidden items

		let filteredItems = allItems;

		if (!currentReseller?.resellerView) {
			filteredItems = allItems.filter((item) => {
				const configureKey = configureKeyMapping[item.key];
				return !configureKey || !hiddenConfigureItems.includes(configureKey);
			});
		}

		// Then modify remaining items to handle gating
		return filteredItems.map((item) => {
			const configureKey = configureKeyMapping[item.key];
			const isGated = configureKey && gatedConfigureItems.includes(configureKey);

			if (isGated && !currentReseller?.resellerView) {
				return {
					...item,
					children: (
						<Alert
							message="Feature Locked"
							description="This feature is not available in your current plan. Please upgrade to access it."
							type="warning"
							showIcon
							action={
								<Button
									type="primary"
									onClick={() =>
										dispatch(
											openModal({ modalId: MODAL_IDS.RESELLER_CLIENT_UPGRADE_SUBSCRIPTION_MODAL }),
										)
									}
								>
									Upgrade Now
								</Button>
							}
						/>
					),
				};
			}

			return item;
		});
	}, [
		data,
		mutation,
		postCallWebhookMutation,
		deletePostCallWebhookMutation,
		refetch,
		hiddenConfigureItems,
		gatedConfigureItems,
		dispatch,
	]);

	const handleCardClick = (key: string | string[]) => {
		setActiveKey(key);
	};

	useEffect(() => {
		const newCompletionStatus = items
			//Note: Please keep adding or removing items from here if we want to skip them from "Add Content" completion badge
			.filter(
				(item) =>
					![OverViewCards.TWEAK_ADVANCED_SETTINGS].some(
						(notAllowedItem) => notAllowedItem === item.key,
					),
			)
			.map((item) => {
				switch (item.key) {
					case OverViewCards.GREETING_PHRASE:
						return !!data?.system_phrase;
					case OverViewCards.AI_PROMPT:
						return !!data?.ai_prompt;
					case OverViewCards.SAMPLE_QUESTIONS_ANSWERS:
						return !!data?.sample_qa;
					case OverViewCards.KNOWLEDGE_BASE:
						return !!data?.business_information;
					case OverViewCards.CALL_TRANSFERRING_WORKFLOW:
						return data?.callWorkflows && isNonEmptyArray(safeJSONParse(data?.callWorkflows, []));

					case OverViewCards.TEXTING_WORKFLOW:
						return data?.workflows && isNonEmptyArray(safeJSONParse(data.workflows, []));

					case OverViewCards.EMAIL_WORKFLOW:
						return data?.workflows && isNonEmptyArray(safeJSONParse(data.workflows, []));

					case OverViewCards.CUSTOMIZE_VOICE_LANGUAGES:
						return !!data?.voice_selected && !!data?.selected_languages;
					case OverViewCards.PHONE_FORM_WORKFLOW:
						return (
							(data?.intake_questions ?? data?.intake_questions_workflows) &&
							isNonEmptyArray(
								safeJSONParse(data?.intake_questions ?? data?.intake_questions_workflows, []),
							)
						);
					case OverViewCards.POST_CALL_WEBHOOK:
						return !!data?.postCallWebhook;
					default:
						return false;
				}
			});
		const completedCount = newCompletionStatus.filter(Boolean).length;
		onCompletionChange(completedCount, newCompletionStatus.length);
	}, [data, items, onCompletionChange]);

	useEffect(() => {
		if (data) {
			const counts = initializeScenarioCounts(data);
			dispatch(setScenarioCounts(counts));
		}
	}, [data]);

	useEffect(() => {
		if (calendarData?.data?.calendars) {
			const calendarCount = calendarData.data.calendars.length;
			dispatch(setCalendarCount(calendarCount));
		}
	}, [calendarData]);

	return (
		<div className="overview-card-container p-4">
			{/* Basic Settings Group */}
			<Collapse
				accordion
				defaultActiveKey={['basic-settings']}
				bordered={false}
				className="bg-white parent-accordion mt-2 mb-5 -mx-3 rounded-lg"
				items={[
					{
						key: 'basic-settings',
						label: (
							<div className="flex items-center">
								<div className="p-3 mr-4">
									<MessageOutlined className="text-blue-700" />
								</div>
								<span className="text-lg font-semibold text-gray-900">Basic Settings</span>
							</div>
						),
						children: (
							<div className="p-0">
								<Collapse
									className="bg-white child-accordion"
									accordion
									bordered={false}
									items={getValidItems(items, [
										OverViewCards.GREETING_PHRASE,
										OverViewCards.AI_PROMPT,
										OverViewCards.KNOWLEDGE_BASE,
										OverViewCards.SAMPLE_QUESTIONS_ANSWERS,
										OverViewCards.CUSTOMIZE_VOICE_LANGUAGES,
										OverViewCards.CHOOSE_VOICE,
									])}
									onChange={handleCardClick}
									activeKey={activeKey}
								/>
							</div>
						),
					},
				]}
			/>

			{/* During-Call Actions Group */}
			<Collapse
				accordion
				bordered={false}
				className="bg-white parent-accordion my-5 -mx-3"
				items={[
					{
						key: 'during-call-actions',
						label: (
							<div className="flex items-center">
								<div className="p-3 mr-4">
									<PhoneForwardedOutlined className="text-blue-700" />
								</div>
								<span className="text-lg font-semibold text-gray-900">In-Call Actions</span>
							</div>
						),
						children: (
							<div className="p-0">
								<Collapse
									className="bg-white child-accordion"
									accordion
									bordered={false}
									items={getValidItems(items, [
										OverViewCards.CALL_TRANSFERRING_WORKFLOW,
										OverViewCards.TEXTING_WORKFLOW,
										OverViewCards.EMAIL_WORKFLOW,
										OverViewCards.PHONE_FORM_WORKFLOW,
										OverViewCards.CALENDAR_SCHEDULING_WORKFLOW,
									])}
									onChange={handleCardClick}
									activeKey={activeKey}
								/>
							</div>
						),
					},
				]}
			/>

			{/* After-Call Tasks Group */}

			<Collapse
				accordion
				bordered={false}
				className="bg-white parent-accordion my-5 -mx-3"
				items={[
					{
						key: 'after-call-tasks',
						label: (
							<div className="flex items-center">
								<div className="p-3 mr-4">
									<CallEnd className="text-blue-700" />
								</div>
								<span className="text-lg font-semibold text-gray-900">After-Call Actions</span>
							</div>
						),
						children: (
							<div className="p-0">
								<Collapse
									className="bg-white child-accordion"
									accordion
									bordered={false}
									items={getValidItems(items, [
										OverViewCards.POST_CALL_MESSAGE,
										OverViewCards.TEXT_ASSIST,
										OverViewCards.SMART_CALL_ANALYSIS,
									])}
									onChange={handleCardClick}
									activeKey={activeKey}
								/>
							</div>
						),
					},
				]}
			/>

			{/* Developer Automations Group */}
			<Collapse
				accordion
				bordered={false}
				className="bg-white parent-accordion my-5 -mx-3"
				items={[
					{
						key: 'developer-automations',
						label: (
							<div className="flex items-center">
								<div className="p-3 mr-4">
									<WebhookOutlined className="text-blue-700" />
								</div>
								<span className="text-lg font-semibold text-gray-900">Developer Automations</span>
							</div>
						),
						children: (
							<div className="p-0">
								<Collapse
									className="bg-white child-accordion"
									accordion
									bordered={false}
									items={getValidItems(items, [
										OverViewCards.API_WORKFLOW,
										OverViewCards.ZAPIER_INTEGRATION,
										OverViewCards.POST_CALL_WEBHOOK,
									])}
									onChange={handleCardClick}
									activeKey={activeKey}
								/>
							</div>
						),
					},
				]}
			/>

			{/* Advanced Settings Group */}
			{isNoviUser ? (
				<Collapse
					accordion
					bordered={false}
					className="bg-white parent-accordion my-5 -mx-3"
					items={[
						{
							key: 'advanced-settings',
							label: (
								<div className="flex items-center">
									<div className="p-3 mr-4">
										<PinchOutlined className="text-blue-700" />
									</div>
									<span className="text-lg font-semibold text-gray-900">Advanced Settings</span>
								</div>
							),
							children: (
								<div className="p-0">
									<Collapse
										className="bg-white child-accordion"
										accordion
										bordered={false}
										items={getValidItems(items, [
											OverViewCards.TWEAK_ADVANCED_SETTINGS,
											OverViewCards.CALL_RESPONSE_CONFIG,
										])}
										onChange={handleCardClick}
										activeKey={activeKey}
									/>
								</div>
							),
						},
					]}
				/>
			) : (
				<Collapse
					accordion
					bordered={false}
					className="bg-white parent-accordion my-5 -mx-3"
					items={[
						{
							key: OverViewCards.TWEAK_ADVANCED_SETTINGS,
							label: (
								<div className="flex items-center">
									<div className="p-3 mr-4">
										<PinchOutlined className="text-blue-700" />
									</div>
									<span className="text-lg font-semibold text-gray-900">Advanced Settings</span>
								</div>
							),
							children: (
								<div ref={tweakAdvancedSettingsRef}>
									<TweakAdvancedSettingsInfo
										data={data}
										refetch={refetch}
										debrand={debrand}
										isNoviUser={isNoviUser}
									/>
								</div>
							),
						},
					]}
					onChange={handleCardClick}
					activeKey={activeKey}
				/>
			)}

			{/* Remaining items */}
			<Collapse
				accordion
				className="[&_.ant-collapse-item]:border-[#f0f0f0]/50 [&_.ant-collapse-content]:border-[#f0f0f0]/50 [&_.ant-collapse-header]:py-4 [&_.ant-collapse-item]:mb-3 [&_.ant-collapse-item]:rounded-lg [&_.ant-collapse-item]:shadow-sm [&_.ant-collapse-header]:rounded-lg hover:[&_.ant-collapse-item]:shadow-md transition-shadow duration-200"
				items={items.filter(
					(item) =>
						![
							OverViewCards.GREETING_PHRASE,
							OverViewCards.AI_PROMPT,
							OverViewCards.KNOWLEDGE_BASE,
							OverViewCards.SAMPLE_QUESTIONS_ANSWERS,
							OverViewCards.CUSTOMIZE_VOICE_LANGUAGES,
							OverViewCards.CALL_TRANSFERRING_WORKFLOW,
							OverViewCards.TEXTING_WORKFLOW,
							OverViewCards.EMAIL_WORKFLOW,
							OverViewCards.PHONE_FORM_WORKFLOW,
							OverViewCards.CALENDAR_SCHEDULING_WORKFLOW,
							OverViewCards.API_WORKFLOW,
							OverViewCards.POST_CALL_WEBHOOK,
							OverViewCards.POST_CALL_MESSAGE,
							OverViewCards.TWEAK_ADVANCED_SETTINGS,
							OverViewCards.CALL_RESPONSE_CONFIG,
							OverViewCards.TEXT_ASSIST,
							OverViewCards.ZAPIER_INTEGRATION,
							OverViewCards.SMART_CALL_ANALYSIS,
							OverViewCards.CHOOSE_VOICE,
						].includes(item.key),
				)}
				onChange={handleCardClick}
				activeKey={activeKey}
			/>
		</div>
	);
};

export default OverviewCard;
